import { useDepartmentText } from 'model/DepartmentTerminology';
import { useForm, UseFormReturnType } from '@mantine/form';
import { RevisionGetResponse as Revision } from 'api/actions/revision-get/revision-get-response';
import {
  IForm,
  IFormInput,
  IFormInputSpecDeviceEvidenceField,
  IFormSection,
  IFormTabSlug,
} from 'pages/revisions-module/template-editor/editors/form/types';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { RevisionChange, RevisionChangeGroup, RevisionFormData } from 'components/forms/revision/fill-out/types';
import { fromPairs, has, times, values, zip } from 'lodash';
import createValidator from 'components/forms/validators/create-validator';
import required from 'components/forms/validators/rules/rule-required';
import { useFileManager } from 'api/file-manager/file-manager-context';
import toBase64 from 'utils/to-base64';
import panic from 'errors/panic';
import useTotalFaultsIntervalCheck from 'components/forms/revision/fill-out/data/hooks/use-total-faults-interval-check';
import { FEATURE_TOGGLE_REVISION_CHANGES_MODAL, FEATURE_TOGGLE_TECHNICIAN_COPY_REVISION_DATA } from 'env';
import { FORM_PAGE_PATH, REVISION_PAGE_PATH } from 'routes/paths';
import { useDisclosure } from '@mantine/hooks';
import { getEvidenceFieldValue } from 'utils/evidence-field';
import { formatDepartmentPath } from 'components/department/DepartmentBreadcrumb';
import { resolveDepartmentPlaceholderFields } from 'pages/revisions-module/template-editor/editors/template/extensions/placeholder/department-placeholders';

export interface IExtendedFormInput extends IFormInput {
  tabTitle: string;
}

type ISectionsByTab = Record<IFormTabSlug, IFormSection[]>;
type IInputLookup = Map<string, IExtendedFormInput>;

interface IFillOutRevisionDataProviderContext {
  revision: Revision;
  sectionsByTab: ISectionsByTab;
  form: UseFormReturnType<RevisionFormData>;
  formKey: number;
  changes: RevisionChangeGroup[];
  changesAcknowledged: boolean;
  dataInitialized: boolean;
  findInputByName: (name: string) => IExtendedFormInput | undefined;
  tabHasVisibleInputs: (slug: IFormTabSlug) => boolean;
  revisionHasDevice: (deviceId: number) => boolean;
  refreshForm: () => void;
  acknowledgedChanges: () => void;
  markDataInitialized: () => void;
}

export const TAB_TITLE_MAP: Record<IFormTabSlug, string> = {
  info: 'Základné informácie',
  description: 'Popis',
  specification: 'Špecifikácia',
  conclusion: 'Záver',
  device: 'Zariadenia',
  measurements: 'Merania',
  faults: 'Nedostatky',
  attachments: 'Prílohy',
};

const FillOutRevisionDataProviderContext = createContext<IFillOutRevisionDataProviderContext>(undefined!);

/**
 * Provides the revision and form spec to the children.
 */
export function FillOutRevisionDataProvider({ revision, children }: { revision: Revision; children: ReactNode }) {
  const departmentText = useDepartmentText();
  const { readFile } = useFileManager();
  const [ready, setReady] = useState(false);
  const [changesAcknowledged, { open: acknowledgedChanges }] = useDisclosure(!FEATURE_TOGGLE_REVISION_CHANGES_MODAL);
  const [dataInitialized, { open: markDataInitialized }] = useDisclosure(!FEATURE_TOGGLE_TECHNICIAN_COPY_REVISION_DATA);

  const [formKey, setFormKey] = useState(0);
  const refreshForm = useCallback(() => setFormKey((prev) => prev + 1), []);

  const formSpec = useMemo(() => JSON.parse(revision.revisionTemplate!.formScheme!) as IForm, [revision]);
  const allSections = useMemo(() => formSpec.sections, [formSpec]);
  const allRows = useMemo(() => allSections.flatMap(({ rows }) => rows), [allSections]);
  const allInputs = useMemo(() => allRows.flatMap(({ inputs }) => inputs), [allRows]);

  const sectionsByTab: ISectionsByTab = useMemo(
    () => ({
      info: allSections.filter(({ tab }) => tab === 'info'),
      description: allSections.filter(({ tab }) => tab === 'description'),
      specification: allSections.filter(({ tab }) => tab === 'specification'),
      conclusion: allSections.filter(({ tab }) => tab === 'conclusion'),
      device: allSections.filter(({ tab }) => tab === 'device'),
      measurements: allSections.filter(({ tab }) => tab === 'measurements'),
      faults: allSections.filter(({ tab }) => tab === 'faults'),
      attachments: allSections.filter(({ tab }) => tab === 'attachments'),
    }),
    [allSections]
  );

  const inputLookup = useMemo(() => {
    const lookup: IInputLookup = new Map();

    allSections.forEach(({ rows, tab }) => {
      rows.forEach(({ inputs }) => {
        inputs.forEach((input) => {
          lookup.set(input.spec.name, { ...input, tabTitle: TAB_TITLE_MAP[tab] });
        });
      });
    });

    return lookup;
  }, [allSections]);

  const validate = useMemo(() => {
    const requiredInputs = allInputs.filter(({ spec: { required } }) => required);

    const revisionFields = requiredInputs
      .filter(({ context }) => context === 'revision')
      .map(({ spec: { name } }) => `fields.${name}`);

    const deviceFields = requiredInputs
      .filter(({ context }) => context === 'device')
      .flatMap(({ spec: { name } }) => revision.devices.map((device) => `devices.${device.deviceId}.fields.${name}`));

    const fields = [...revisionFields, ...deviceFields];

    const validators = times(fields.length, () => createValidator([required]));

    return fromPairs(zip(fields, validators));
  }, [allInputs, revision]);

  const initialRevisionData = useMemo(
    () => JSON.parse(revision.revisionData ?? 'null') as RevisionFormData | null,
    [revision]
  );

  const initialValues: RevisionFormData = useMemo(() => {
    let data: RevisionFormData = initialRevisionData || { fields: {}, devices: {} };

    allInputs
      .filter(({ context }) => context === 'revision')
      .filter(({ spec: { name } }) => !has(data.fields, name))
      .forEach(({ spec }) => {
        switch (spec.type) {
          case 'checkbox':
            data.fields[spec.name] = !!spec.defaultChecked;
            break;

          default:
            // No default value.
            break;
        }
      });

    return data;
  }, [revision]);

  const changes: RevisionChangeGroup[] = useMemo(() => {
    if (!FEATURE_TOGGLE_REVISION_CHANGES_MODAL || !initialRevisionData) {
      return [];
    }

    const { fields } = initialValues;
    const changes: RevisionChangeGroup[] = [];

    if (has(fields, '_revisionName') && fields._revisionName !== revision.revisionName) {
      changes.push({
        title: revision.kind === 'revision' ? 'Názov revízie' : 'Názov formulára',
        changes: [{ before: fields._revisionName, after: revision.revisionName }],
      });
    }

    if (has(fields, '_organization') && fields._organization !== revision.organization.fullName) {
      changes.push({
        title: 'Organizácia',
        changes: [{ before: fields._organization, after: revision.organization.fullName }],
      });
    }

    if (has(fields, '_department') && fields._department !== revision.department.fullName) {
      changes.push({
        title: departmentText('Položka úrovne', 'Stredisko'),
        changes: [{ before: fields._department, after: revision.department.fullName }],
      });
    }

    const departmentPath = formatDepartmentPath(revision.department.breadcrumbs);

    if (has(fields, '_departmentPath') && fields._departmentPath !== departmentPath) {
      changes.push({
        title: departmentText('Celá cesta položky úrovne', 'Celá cesta strediska'),
        changes: [{ before: fields._departmentPath, after: departmentPath }],
      });
    }

    const deviceMap = fromPairs(revision.devices.map((device) => [String(device.deviceId), device]));

    const addedDevices = revision.devices.filter((device) => !has(initialValues.devices, String(device.deviceId)));
    const removedDevices = values(initialValues.devices).filter((device) => !has(deviceMap, String(device.deviceId)));
    const keptDevices = values(initialValues.devices).filter((device) => has(deviceMap, String(device.deviceId)));

    if (addedDevices.length > 0 || removedDevices.length > 0) {
      changes.push({
        title: 'Zariadenia',
        changes: [
          ...addedDevices.map((device) => ({ before: '', after: device.deviceName })),
          ...removedDevices.map((device) => ({ before: device.fields._deviceName, after: '' })),
        ],
      });
    }

    keptDevices.forEach((device) => {
      const revisionDevice = deviceMap[String(device.deviceId)];

      const { fields } = device;
      const deviceChanges: RevisionChange[] = [];

      if (has(fields, '_deviceName') && fields._deviceName !== revisionDevice.deviceName) {
        deviceChanges.push({ before: fields._deviceName, after: revisionDevice.deviceName });
      }

      if (has(fields, '_deviceLocation') && fields._deviceLocation !== revisionDevice.location) {
        deviceChanges.push({
          subTitle: 'Umiestnenie',
          before: fields._deviceLocation,
          after: revisionDevice.location,
        });
      }

      if (has(fields, '_deviceManufacturer') && fields._deviceManufacturer !== revisionDevice.manufacturer) {
        deviceChanges.push({
          subTitle: 'Výrobca',
          before: fields._deviceManufacturer,
          after: revisionDevice.manufacturer,
        });
      }

      if (has(fields, '_deviceManufactured') && fields._deviceManufactured !== revisionDevice.manufactured) {
        deviceChanges.push({
          subTitle: 'Rok výroby',
          before: fields._deviceManufactured,
          after: revisionDevice.manufactured,
        });
      }

      if (has(fields, '_deviceSerialNumber') && fields._deviceSerialNumber !== revisionDevice.serialNumber) {
        deviceChanges.push({
          subTitle: 'Výrobné číslo',
          before: fields._deviceSerialNumber,
          after: revisionDevice.serialNumber,
        });
      }

      if (has(fields, '_deviceTypeName') && fields._deviceTypeName !== revisionDevice.deviceType?.deviceTypeName) {
        deviceChanges.push({
          subTitle: 'Skupina zariadenia',
          before: fields._deviceTypeName,
          after: revisionDevice.deviceType?.deviceTypeName,
        });
      }

      if (
        has(fields, '_deviceSubTypeName') &&
        fields._deviceSubTypeName !== revisionDevice.deviceSubtype?.deviceTypeName
      ) {
        deviceChanges.push({
          subTitle: 'Typ zariadenia',
          before: fields._deviceSubTypeName,
          after: revisionDevice.deviceSubtype?.deviceTypeName,
        });
      }

      if (has(fields, '_deviceBuilding') && fields._deviceBuilding !== revisionDevice.building) {
        deviceChanges.push({
          subTitle: 'Budova',
          before: fields._deviceBuilding,
          after: revisionDevice.building,
        });
      }

      if (has(fields, '_deviceFloor') && fields._deviceFloor !== revisionDevice.floor) {
        deviceChanges.push({
          subTitle: 'Poschodie',
          before: fields._deviceFloor,
          after: revisionDevice.floor,
        });
      }

      if (has(fields, '_deviceRoom') && fields._deviceRoom !== revisionDevice.room) {
        deviceChanges.push({
          subTitle: 'Miestnosť',
          before: fields._deviceRoom,
          after: revisionDevice.room,
        });
      }

      if (has(fields, '_deviceLatitude') && fields._deviceLatitude !== revisionDevice.latitude) {
        deviceChanges.push({
          subTitle: 'Zemepisná šírka',
          before: fields._deviceLatitude,
          after: revisionDevice.latitude,
        });
      }

      if (has(fields, '_deviceLongitude') && fields._deviceLongitude !== revisionDevice.longitude) {
        deviceChanges.push({
          subTitle: 'Zemepisná dĺžka',
          before: fields._deviceLongitude,
          after: revisionDevice.longitude,
        });
      }

      if (
        has(fields, '_deviceInternalPossessionsNumber') &&
        fields._deviceInternalPossessionsNumber !== revisionDevice.internalPossessionsNumber
      ) {
        deviceChanges.push({
          subTitle: 'Interné číslo majetku',
          before: fields._deviceInternalPossessionsNumber,
          after: revisionDevice.internalPossessionsNumber,
        });
      }

      if (has(fields, '_deviceInternalNote') && fields._deviceInternalNote !== revisionDevice.internalNote) {
        deviceChanges.push({
          subTitle: 'Interná poznámka',
          before: fields._deviceInternalNote,
          after: revisionDevice.internalNote,
        });
      }

      // Check for evidence field changes
      const deviceEvidenceFieldInputs = allInputs.filter(
        (input) => input.context === 'device' && input.spec.type === 'deviceEvidenceField'
      );

      deviceEvidenceFieldInputs.forEach((input) => {
        const spec = input.spec as IFormInputSpecDeviceEvidenceField;
        const fieldName = spec.name;

        if (has(fields, fieldName)) {
          const storedValue = fields[fieldName];
          const evidenceField = revisionDevice.evidenceFields.find(
            (field) => field.evidenceFieldId === Number(spec.evidenceFieldId)
          );

          if (evidenceField) {
            let currentValue: string | number | boolean = getEvidenceFieldValue(evidenceField) ?? '';

            // Format number values with precision
            if (evidenceField.fieldType === 'number') {
              currentValue = Number(currentValue)
                .toFixed(evidenceField.precision || 0)
                .replace('.', ',');
            } else if (evidenceField.fieldType === 'checkbox') {
              currentValue = currentValue ? 'Áno' : 'Nie';
            }

            // Convert to string for comparison
            const storedValueStr = String(storedValue);
            const currentValueStr = String(currentValue);

            if (storedValueStr !== currentValueStr) {
              deviceChanges.push({
                subTitle: spec.label || evidenceField.fieldName || fieldName,
                before: storedValueStr,
                after: currentValueStr,
              });
            }
          }
        }
      });

      if (deviceChanges.length > 0) {
        changes.push({
          title: `Zariadenie ${fields._deviceName} (ID: ${device.deviceId})`,
          changes: deviceChanges,
        });
      }
    });

    return changes;
  }, [departmentText, initialValues, revision]);

  const form = useForm<RevisionFormData>({
    initialValues,
    validate,
  });

  const findInputByName = useCallback((name: string) => inputLookup.get(name), [inputLookup]);

  const tabHasVisibleInputs = useCallback(
    (tab: IFormTabSlug) =>
      sectionsByTab[tab].some(({ rows }) => rows.some(({ inputs }) => inputs.some(({ spec: { hidden } }) => !hidden))),
    [sectionsByTab]
  );

  const revisionHasDevice = useCallback(
    (deviceId: number) => revision.devices.some((device) => device.deviceId === deviceId),
    [revision]
  );

  // Fill in the form with the revision data.
  useEffect(() => {
    form.setFieldValue('fields._revisionName', revision.revisionName);
    form.setFieldValue('fields._revisionId', revision.revisionId);
    form.setFieldValue(
      'fields._revisionURL',
      window.location.origin +
        (revision.kind === 'revision'
          ? REVISION_PAGE_PATH.insert({ revisionId: revision.revisionId })
          : FORM_PAGE_PATH.insert({ revisionId: revision.revisionId }))
    );
    form.setFieldValue('fields._organization', revision.organization.fullName);
    form.setFieldValue('fields._department', revision.department.fullName);
    form.setFieldValue('fields._departmentPath', formatDepartmentPath(revision.department.breadcrumbs));
    for (const [name, value] of Object.entries(
      resolveDepartmentPlaceholderFields(revision.department.breadcrumbs, initialValues.fields)
    )) {
      form.setFieldValue(`fields.${name}`, value);
    }
    form.setFieldValue('fields._deviceCount', revision.devices.length);

    form.setFieldValue('fields._organizationName', revision.organization.fullName);
    form.setFieldValue('fields._organizationOfficialName', revision.organization.officialName);
    form.setFieldValue('fields._organizationIco', revision.organization.ico);
    form.setFieldValue('fields._organizationDic', revision.organization.dic);
    form.setFieldValue('fields._organizationIcdph', revision.organization.icdph);

    form.setFieldValue('fields._technicianName', revision.technician!.fullName);
    form.setFieldValue('fields._technicianOrganization', revision.technician!.organization.fullName);
    form.setFieldValue('fields._technicianPhone', revision.technician!.phone);
    form.setFieldValue('fields._technicianEmail', revision.technician!.email);

    form.setFieldValue('fields._technicianOrganizationName', revision.technician!.organization.fullName);
    form.setFieldValue('fields._technicianOrganizationOfficialName', revision.technician!.organization.officialName);
    form.setFieldValue('fields._technicianOrganizationIco', revision.technician!.organization.ico);
    form.setFieldValue('fields._technicianOrganizationDic', revision.technician!.organization.dic);
    form.setFieldValue('fields._technicianOrganizationIcdph', revision.technician!.organization.icdph);

    form.setFieldValue('fields._assignerOrganizationName', revision.assignedBy.fullName);
    form.setFieldValue('fields._assignerOrganizationOfficialName', revision.assignedBy.officialName);
    form.setFieldValue('fields._assignerOrganizationIco', revision.assignedBy.ico);
    form.setFieldValue('fields._assignerOrganizationDic', revision.assignedBy.dic);
    form.setFieldValue('fields._assignerOrganizationIcdph', revision.assignedBy.icdph);

    form.setFieldValue('fields._technicianSignatureFileId', revision.technician!.signatureFileId);

    if (revision.technician!.certificates.length > 0) {
      const [certificate] = revision.technician!.certificates;
      form.setFieldValue('fields._technicianCertificateNumber', certificate.certificateNumber);
      form.setFieldValue('fields._technicianCertificateRenewalDate', certificate.renewalDate);
      form.setFieldValue('fields._technicianCertificateStampFileId', certificate.stampFileId);
    }

    revision.devices.forEach((device) => {
      if (!form.values.devices[device.deviceId]) {
        form.setFieldValue(`devices.${device.deviceId}`, {
          deviceId: device.deviceId,
          fields: {},
          faults: [],
          measurements: {},
        });
      }

      const prefix = `devices.${device.deviceId}.fields`;

      form.setFieldValue(`${prefix}._deviceName`, device.deviceName);
      form.setFieldValue(`${prefix}._deviceLocation`, device.location);
      form.setFieldValue(`${prefix}._deviceManufacturer`, device.manufacturer);
      form.setFieldValue(`${prefix}._deviceManufactured`, device.manufactured);
      form.setFieldValue(`${prefix}._deviceSerialNumber`, device.serialNumber);
      form.setFieldValue(`${prefix}._deviceTypeName`, device.deviceType?.deviceTypeName);
      form.setFieldValue(`${prefix}._deviceSubTypeName`, device.deviceSubtype?.deviceTypeName);
      form.setFieldValue(`${prefix}._deviceBuilding`, device.building);
      form.setFieldValue(`${prefix}._deviceFloor`, device.floor);
      form.setFieldValue(`${prefix}._deviceRoom`, device.room);
      form.setFieldValue(`${prefix}._deviceLatitude`, device.latitude);
      form.setFieldValue(`${prefix}._deviceLongitude`, device.longitude);
      form.setFieldValue(`${prefix}._deviceInternalPossessionsNumber`, device.internalPossessionsNumber);
      form.setFieldValue(`${prefix}._deviceInternalNote`, device.internalNote);
      form.setFieldValue(`${prefix}._deviceRemoteSourceId`, device.remoteSource?.remoteSourceId);
      form.setFieldValue(`${prefix}._deviceRemoteSourceType`, device.remoteSource?.type);
      form.setFieldValue(`${prefix}._deviceExternalId`, device.externalId);
      form.setFieldValue(`${prefix}._isParentDevice`, device.structure === 'parent');
      form.setFieldValue(`${prefix}._isChildDevice`, device.structure === 'child');
      form.setFieldValue(`${prefix}._deviceStructure`, device.structure);
    });

    Object.keys(form.values.devices).forEach((deviceId) => {
      const revisionHasDevice = revision.devices.find((device) => device.deviceId === Number(deviceId));

      if (!revisionHasDevice) {
        form.setFieldValue(`devices.${deviceId}.removed`, true);
      }
    });

    allInputs.forEach(({ spec }) => {
      if (spec.type === 'deviceProperty') {
        revision.devices.forEach((device) => {
          const field = `devices.${device.deviceId}.fields.${spec.name}`;

          const value =
            spec.property === 'deviceSubTypeName' ? device.deviceSubtype?.deviceTypeName : device[spec.property];

          form.setFieldValue(field, value);
        });
      }
    });

    setReady(true);
  }, [revision]);

  // Fetch the technician certificate stamp.
  useEffect(() => {
    const fileId = form.values.fields._technicianCertificateStampFileId;

    if (fileId) {
      readFile({ fileId })
        .then(toBase64)
        .then((parsed) => form.setFieldValue('fields._technicianCertificateStamp', parsed))
        .catch(panic);
    }
  }, [form.values.fields._technicianCertificateStampFileId]);

  // Fetch the organization logo.
  useEffect(() => {
    const fileId = revision.organization.logoFileId;

    if (fileId) {
      readFile({ fileId })
        .then(toBase64)
        .then((parsed) => form.setFieldValue('fields._organizationLogo', parsed))
        .catch(panic);
    }
  }, [revision.organization.logoFileId]);

  // Fetch the technician organization logo.
  useEffect(() => {
    const fileId = revision.technician!.organization.logoFileId;

    if (fileId) {
      readFile({ fileId })
        .then(toBase64)
        .then((parsed) => form.setFieldValue('fields._technicianOrganizationLogo', parsed))
        .catch(panic);
    }
  }, [revision.technician!.organization.logoFileId]);

  // Fetch the assigner organization logo.
  useEffect(() => {
    const fileId = revision.assignedBy.logoFileId;

    if (fileId) {
      readFile({ fileId })
        .then(toBase64)
        .then((parsed) => form.setFieldValue('fields._assignerOrganizationLogo', parsed))
        .catch(panic);
    }
  }, [revision.assignedBy.logoFileId]);

  // Interval checks.
  useTotalFaultsIntervalCheck(form);

  const value = useMemo(
    () => ({
      revision,
      sectionsByTab,
      form,
      formKey,
      changes,
      changesAcknowledged,
      dataInitialized,
      findInputByName,
      tabHasVisibleInputs,
      revisionHasDevice,
      refreshForm,
      acknowledgedChanges,
      markDataInitialized,
    }),
    [
      revision,
      sectionsByTab,
      form,
      formKey,
      changes,
      changesAcknowledged,
      dataInitialized,
      findInputByName,
      tabHasVisibleInputs,
      revisionHasDevice,
      refreshForm,
      acknowledgedChanges,
      markDataInitialized,
    ]
  );

  return (
    <FillOutRevisionDataProviderContext.Provider value={value}>
      {ready ? children : <></>}
    </FillOutRevisionDataProviderContext.Provider>
  );
}

/**
 * Hook to get the revision and form spec.
 */
export function useFillOutRevisionDataProvider() {
  return useContext(FillOutRevisionDataProviderContext);
}
